import React, { useEffect, useState } from 'react';
import {
    Box, Button, Chip, Stack, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckIcon from '@mui/icons-material/Check';
import { useTBContext } from '../../../Contexts/useTBStates';
import { RoundButton } from '../../TabComponents';
import { useFetchPaceData } from '../../../hooks/useFetchPaceData';
import type { PaceOfPlayRow } from '@moment-golf/types';
import { computeTeeTimePaceMetrics } from '@moment-golf/shared';

interface PaceOfPlayPanelProps {
    appDims: any;
    compHeights: any;
}

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const TICK_INTERVAL_MS = 60 * 1000;

function formatDuration(ms: number): string {
    if (ms < 0) ms = 0;
    const totalMinutes = Math.floor(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
}

function getPaceClocks(row: PaceOfPlayRow) {
    return row.paceClocks?.length
        ? row.paceClocks
        : row.startTime
            ? [{ scoreCode: null as string | null, startTime: row.startTime, endTime: row.endTime }]
            : [];
}

/** Player is done when their pace clock (or shared group clock) has an endTime. */
function isPlayerDone(row: PaceOfPlayRow, playerScoreCode: string, isOpponent: boolean): boolean {
    const clocks = getPaceClocks(row);
    if (isOpponent) {
        return clocks.some((c) => c.scoreCode === playerScoreCode && !!c.endTime);
    }
    return clocks.some((c) => c.scoreCode == null && !!c.endTime);
}

function computePaceDisplay(row: PaceOfPlayRow, targetHours: number, nowMs: number) {
    const effectiveNowMs = row.endTime
        ? Math.min(nowMs, new Date(row.endTime).getTime())
        : nowMs;

    const metrics = computeTeeTimePaceMetrics(
        effectiveNowMs,
        row.totalHoles,
        targetHours,
        row.players.map((p) => ({ scoreCode: p.scoreCode, holesCompleted: p.holesCompleted })),
        getPaceClocks(row),
    );

    if (!metrics.hasPace) {
        return {
            elapsed: '—',
            projected: '—',
            isOOP: false,
            metrics,
        };
    }

    return {
        elapsed: formatDuration(metrics.avgElapsedMs),
        projected: metrics.avgProjectedMs > 0 ? formatDuration(metrics.avgProjectedMs) : '—',
        isOOP: metrics.isOOP,
        metrics,
    };
}

function LabeledMetric({ label, value }: { label: string; value: string }) {
    return (
        <Stack spacing={0.25} alignItems='flex-start'>
            <Typography
                variant='caption'
                color='text.secondary'
                sx={{ fontSize: 10, lineHeight: 1.1, textTransform: 'uppercase', letterSpacing: 0.3 }}
            >
                {label}
            </Typography>
            <Typography variant='body2' sx={{ fontWeight: 600, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                {value}
            </Typography>
        </Stack>
    );
}

const PLAYER_ROW_PX = 28;
const CARD_HEADER_PX = 72;
const CARD_BODY_PADDING_PX = 16;

export const PaceOfPlayPanel = ({ compHeights }: PaceOfPlayPanelProps) => {
    const {
        viewEvent, roundValue, setRoundValue,
        paceData, setPaceDataLastFetched,
        appTheme, factor, editMode,
    } = useTBContext();

    const { fetchPaceData } = useFetchPaceData();
    const [loading, setLoading] = useState(false);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        setLoading(true);
        fetchPaceData(true)
            .catch((err) => console.error('[PaceOfPlayPanel] fetch error', err))
            .finally(() => setLoading(false));
    }, [viewEvent, roundValue]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const interval = setInterval(() => {
            fetchPaceData(false).catch((err) =>
                console.error('[PaceOfPlayPanel] poll error', err),
            );
        }, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [fetchPaceData]);

    useEffect(() => {
        const interval = setInterval(() => setTick((t) => t + 1), TICK_INTERVAL_MS);
        return () => clearInterval(interval);
    }, []);

    const handleRefresh = () => {
        setPaceDataLastFetched(null);
        setLoading(true);
        fetchPaceData(true)
            .catch((err) => console.error('[PaceOfPlayPanel] refresh error', err))
            .finally(() => setLoading(false));
    };

    const targetHours: number =
        viewEvent?.rounds?.[roundValue - 1]?.targetRoundLength ??
        viewEvent?.roundLengthTarget ?? 4;

    const isOpponentScoring = viewEvent?.scoreType?.toLowerCase() === 'opponent';

    const nowMs = Date.now() + tick * 0;

    const sortedRows = [...paceData].sort(
        (a, b) => new Date(a.teeTime).getTime() - new Date(b.teeTime).getTime(),
    );

    const maxPlayerCount = Math.max(
        ...sortedRows.map((r) => Math.max(r.players.length, 1)),
        1,
    );
    const cardMinHeight = CARD_HEADER_PX + maxPlayerCount * PLAYER_ROW_PX + CARD_BODY_PADDING_PX;

    return (
        <Stack
            direction='column'
            sx={{
                width: compHeights.appUsableWidth - 60,
                maxHeight: '100%',
                overflow: 'auto',
                padding: '10px 20px',
            }}
            spacing={1.5}
        >
            <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 1 }}>
                <Typography variant='h6' sx={{ fontWeight: 500, color: 'primary.main' }}>
                    Pace of Play
                </Typography>
                <Stack direction='row' spacing={1} alignItems='center'>
                    {viewEvent && (
                        <Stack direction='row' spacing={0} alignItems='center'>
                            {Array.from({ length: Number(viewEvent.totalRounds) }, (_, i) => i + 1).map((n) => (
                                <RoundButton
                                    key={n}
                                    number={n}
                                    roundValue={roundValue}
                                    editMode={editMode}
                                    appTheme={appTheme}
                                    factor={factor}
                                    setRoundValue={setRoundValue}
                                />
                            ))}
                        </Stack>
                    )}
                    <Button
                        variant='outlined'
                        size='small'
                        startIcon={<RefreshIcon />}
                        disabled={loading}
                        onClick={handleRefresh}
                        sx={{ minWidth: 110 }}
                    >
                        {loading ? 'Loading…' : 'Refresh'}
                    </Button>
                </Stack>
            </Stack>

            {!viewEvent && (
                <Typography variant='body2' color='text.secondary'>
                    Select an event to view pace of play.
                </Typography>
            )}

            {viewEvent && sortedRows.length === 0 && !loading && (
                <Typography variant='body2' color='text.secondary'>
                    No pace data yet. Start pace clocks from the First Tee view, or players will
                    auto-start when they submit their first score.
                </Typography>
            )}

            {viewEvent && sortedRows.length > 0 && (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                        gap: 1.5,
                        alignItems: 'stretch',
                    }}
                >
                    {sortedRows.map((row) => {
                        const { elapsed, projected, isOOP, metrics } = computePaceDisplay(row, targetHours, nowMs);
                        const playerProjectedMs = new Map(
                            metrics.playerMetrics.map((m) => [m.scoreCode, m.projectedMs]),
                        );
                        const teeTimeDisplay = row.teeTime
                            ? new Date(row.teeTime).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                                timeZone: 'UTC',
                              })
                            : '—';

                        return (
                            <Box
                                key={row.teeTime}
                                sx={{
                                    border: '1px solid',
                                    borderColor: isOOP ? 'error.light' : 'divider',
                                    borderRadius: 1,
                                    backgroundColor: 'sepia.background',
                                    boxShadow: 2,
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: '100%',
                                    minHeight: cardMinHeight,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'minmax(0, 1fr) 64px 64px min-content',
                                        gap: 1.5,
                                        alignItems: 'start',
                                        px: 1.5,
                                        py: 1.25,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        flexShrink: 0,
                                    }}
                                >
                                    <Stack direction='row' spacing={0.5} alignItems='flex-start' minWidth={0}>
                                        <AccessTimeIcon
                                            sx={{
                                                fontSize: 14,
                                                color: row.startTime ? 'primary.main' : 'text.disabled',
                                                flexShrink: 0,
                                                mt: 0.25,
                                            }}
                                        />
                                        <Stack spacing={0.25} alignItems='flex-start' minWidth={0}>
                                            <Typography
                                                variant='caption'
                                                color='text.secondary'
                                                sx={{ fontSize: 10, lineHeight: 1.1, textTransform: 'uppercase', letterSpacing: 0.3 }}
                                            >
                                                Tee time
                                            </Typography>
                                            <Typography variant='subtitle2' sx={{ fontWeight: 600 }} noWrap>
                                                {teeTimeDisplay}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                    <LabeledMetric label='Elapsed' value={elapsed} />
                                    <LabeledMetric label='Projected' value={projected} />
                                    <Box sx={{ justifySelf: 'start' }}>
                                        {!row.startTime ? (
                                            <Chip label='Not started' size='small' color='warning' variant='outlined' sx={{ fontSize: 10, height: 24 }} />
                                        ) : row.confirmedAt ? (
                                            <Chip label='Confirmed' size='small' color='success' sx={{ fontSize: 10, height: 24 }} />
                                        ) : row.endTime ? (
                                            <Chip label='Done' size='small' color='success' sx={{ fontSize: 10, height: 24 }} />
                                        ) : isOOP ? (
                                            <Chip label='OOP' size='small' color='error' sx={{ fontSize: 10, height: 24 }} />
                                        ) : (
                                            <Chip label='On pace' size='small' color='success' variant='outlined' sx={{ fontSize: 10, height: 24 }} />
                                        )}
                                    </Box>
                                </Box>

                                <Stack
                                    spacing={0.5}
                                    sx={{
                                        px: 1.5,
                                        py: 1,
                                        flex: 1,
                                        minHeight: maxPlayerCount * PLAYER_ROW_PX,
                                        justifyContent: 'flex-start',
                                    }}
                                >
                                    {row.players.length === 0 ? (
                                        <Typography variant='body2' color='text.secondary'>
                                            —
                                        </Typography>
                                    ) : (
                                        row.players.map((player) => {
                                            const projectedMs = playerProjectedMs.get(player.scoreCode);
                                            const playerDone = isPlayerDone(row, player.scoreCode, isOpponentScoring);
                                            return (
                                            <Stack
                                                key={player.scoreCode || player.playerName}
                                                direction='row'
                                                justifyContent='space-between'
                                                alignItems='center'
                                                sx={{ minWidth: 0, minHeight: PLAYER_ROW_PX - 4 }}
                                            >
                                                <Stack direction='row' spacing={0.5} alignItems='center' sx={{ minWidth: 0, pr: 1, flex: 1 }}>
                                                    {playerDone && (
                                                        <CheckIcon
                                                            sx={{ fontSize: 14, color: 'success.main', flexShrink: 0 }}
                                                            aria-label='Done'
                                                        />
                                                    )}
                                                    <Typography
                                                        variant='body2'
                                                        noWrap
                                                        title={player.playerName}
                                                        sx={{ minWidth: 0 }}
                                                    >
                                                        {player.playerName}
                                                    </Typography>
                                                </Stack>
                                                <Stack direction='row' spacing={1} alignItems='center' sx={{ flexShrink: 0 }}>
                                                    <Typography
                                                        variant='body2'
                                                        color='text.secondary'
                                                        sx={{ fontWeight: 500 }}
                                                    >
                                                        {player.holesCompleted}/{row.totalHoles}
                                                    </Typography>
                                                    {projectedMs != null && (
                                                        <Typography
                                                            variant='caption'
                                                            color='text.secondary'
                                                            sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}
                                                        >
                                                            {formatDuration(projectedMs)}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Stack>
                                            );
                                        })
                                    )}
                                </Stack>
                            </Box>
                        );
                    })}
                </Box>
            )}

            {viewEvent && sortedRows.length > 0 && (
                <Typography variant='caption' color='text.secondary' sx={{ mt: 1 }}>
                    Target round length: {targetHours}h · Times UTC · Polling every 5 minutes
                </Typography>
            )}
        </Stack>
    );
};
